import { useCallback } from 'react';
import { useLoadingSpinnerStore } from 'src/store/util/loadingSpinnerUtil';
import { showSuccess, showWarning } from 'src/utils/toast/toastUtil';
import { formatDate } from 'src/utils/string/stringUtil';
import {
	getTQC2010_R01AllCount,
	getTQC2010_R01DetailList,
	getTQC2010_R01List,
	saveTQC2010_R01,
} from './Api_TQC2010_R01';
import {
	createInfoFromRow,
	defaultTQC2010_R01InfoDto,
	TQC2010_R01HeaderDto,
} from './Dto_TQC2010_R01';
import { useTQC2010_R01Store } from './Store_TQC2010_R01';
import useFetchActionbarAuth from 'src/hooks/useFetchActionbarAuth';

interface ContentProps {
	setMessage?: (message: string) => void;
}

export const Content_TQC2010_R01 = ({ setMessage }: ContentProps = {}) => {
	const { setLoadingSpinner } = useLoadingSpinnerStore();
	useFetchActionbarAuth('TQC2010_R01');
	const {
		TQC2010_R01Filter,
		TQC2010_R01List,
		setTQC2010_R01List,
		selectedTQC2010_R01,
		setSelectedTQC2010_R01,
		setAllCount,
		setScrollTop,
		editTQC2010_R01,
		setEditTQC2010_R01,
		TQC2010_R01DetailList,
		setTQC2010_R01DetailList,
		setOriginalTQC2010_R01DetailList,
		setDetailScrollTop,
	} = useTQC2010_R01Store();

	/**
	 * 검사항목 상세 조회
	 */
	const fetchDetailList = useCallback(async (row: TQC2010_R01HeaderDto, inspectType: string) => {
		const response = await getTQC2010_R01DetailList(row.itemCd, row.productOrderNo, inspectType);
		return response.data.data ?? [];
	}, []);

	/**
	 * 테이블 행 클릭 이벤트 핸들러
	 */
	const handleTableRowClick = useCallback(async (row: TQC2010_R01HeaderDto) => {
		setSelectedTQC2010_R01(row);
		setDetailScrollTop(null);

		const inspectType = TQC2010_R01Filter.inspectType;
		const baseInfo = { ...createInfoFromRow(row), inspectType };

		if (!row.itemCd || !inspectType) {
			setEditTQC2010_R01(baseInfo);
			setTQC2010_R01DetailList([]);
			setOriginalTQC2010_R01DetailList([]);
			if (!inspectType) {
				showWarning('검사구분을 먼저 선택하세요.');
			}
			return;
		}

		try {
			const detailList = await fetchDetailList(row, inspectType);
			setTQC2010_R01DetailList(detailList);
			setOriginalTQC2010_R01DetailList(detailList);

			const savedHeader = detailList.find((item) => !!item.inspectorEmp);
			setEditTQC2010_R01(
				savedHeader
					? {
						...baseInfo,
						inspectorEmp: savedHeader.inspectorEmp || '',
						inspectorEmpNm: savedHeader.inspectorEmpNm || '',
						inspectDt: savedHeader.inspectDt || baseInfo.inspectDt,
						inspectTime: savedHeader.inspectTime ? savedHeader.inspectTime.slice(0, 5) : baseInfo.inspectTime,
					}
					: baseInfo,
			);
		} catch (error) {
			console.error('검사항목 상세 조회 실패:', error);
			setEditTQC2010_R01(baseInfo);
			setTQC2010_R01DetailList([]);
			setOriginalTQC2010_R01DetailList([]);
		}
	}, [
		TQC2010_R01Filter.inspectType,
		fetchDetailList,
		setDetailScrollTop,
		setEditTQC2010_R01,
		setOriginalTQC2010_R01DetailList,
		setSelectedTQC2010_R01,
		setTQC2010_R01DetailList,
	]);
	/**
	 * 서브 패널 초기화
	 */
	const clearSubPanels = useCallback(() => {
		setSelectedTQC2010_R01(undefined);
		setEditTQC2010_R01(defaultTQC2010_R01InfoDto);
		setTQC2010_R01DetailList([]);
		setOriginalTQC2010_R01DetailList([]);
	}, [
		setEditTQC2010_R01,
		setOriginalTQC2010_R01DetailList,
		setSelectedTQC2010_R01,
		setTQC2010_R01DetailList,
	]);

	/**
	 * 데이터 조회
	 */
	const fetchData = useCallback(async () => {
		try {
			setScrollTop(null);
			setLoadingSpinner(true);

			const response = await getTQC2010_R01List(
				'search',
				TQC2010_R01Filter,
				TQC2010_R01List.length ?? 0,
			);

			const apiData = response.data.data ?? [];
			setTQC2010_R01List(apiData);

			const countResponse = await getTQC2010_R01AllCount(TQC2010_R01Filter);
			const allCount = countResponse.data.data ?? 0;
			setAllCount(allCount);

			const { TQC2010_R01List: data } = useTQC2010_R01Store.getState();

			if (data.length > 0) {
				await handleTableRowClick(data[0]);
				const message = `조회 완료 (${data.length}/${allCount}건)`;
				showSuccess(message);
				setMessage?.(message);
			} else {
				clearSubPanels();
				showWarning('조회된 데이터가 없습니다.');
				setMessage?.('조회된 데이터가 없습니다.');
			}
		} catch (error) {
			console.error('공정지시 조회 실패:', error);
			showWarning('데이터 조회에 실패했습니다.');
			setTQC2010_R01List([]);
			clearSubPanels();
			setMessage?.('데이터 조회에 실패했습니다.');
		} finally {
			setLoadingSpinner(false);
		}
	}, [
		TQC2010_R01Filter,
		TQC2010_R01List.length,
		clearSubPanels,
		handleTableRowClick,
		setAllCount,
		setLoadingSpinner,
		setScrollTop,
		setTQC2010_R01List,
		setMessage,
	]);

	/**
	 * 검사결과 저장
	 */
	const handleSaveClick = useCallback(async () => {
		const inspectType = TQC2010_R01Filter.inspectType;

		if (!selectedTQC2010_R01) {
			showWarning('작업지시를 먼저 선택하세요.');
			return;
		}
		if (!inspectType) {
			showWarning('검사구분을 먼저 선택하세요.');
			return;
		}
		if (!editTQC2010_R01.inspectorEmp) {
			showWarning('검사자를 입력하세요.');
			return;
		}
		if (TQC2010_R01DetailList.length === 0) {
			showWarning('저장할 검사항목이 없습니다.');
			return;
		}

		setLoadingSpinner(true);
		try {
			await saveTQC2010_R01({
				headerDto: {
					...selectedTQC2010_R01,
					inspectType,
					inspectorEmp: editTQC2010_R01.inspectorEmp,
					inspectorEmpNm: editTQC2010_R01.inspectorEmpNm,
					inspectDt: editTQC2010_R01.inspectDt
						? formatDate(editTQC2010_R01.inspectDt)
						: undefined,
					inspectTime: editTQC2010_R01.inspectTime,
				},
				detailDtoList: TQC2010_R01DetailList,
			});

			const message = '검사결과가 저장되었습니다.';
			showSuccess(message);
			setMessage?.(message);

			const detailList = await fetchDetailList(selectedTQC2010_R01, inspectType);
			setTQC2010_R01DetailList(detailList);
			setOriginalTQC2010_R01DetailList(detailList);
		} catch (error) {
			console.error('검사결과 저장 실패:', error);
			showWarning('검사결과 저장에 실패했습니다.');
		} finally {
			setLoadingSpinner(false);
		}
	}, [
		TQC2010_R01Filter.inspectType,
		selectedTQC2010_R01,
		editTQC2010_R01,
		TQC2010_R01DetailList,
		fetchDetailList,
		setLoadingSpinner,
		setMessage,
		setTQC2010_R01DetailList,
		setOriginalTQC2010_R01DetailList,
	]);

	return { fetchData, handleTableRowClick, handleSaveClick };
};
